import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import Hero from "./Hero";
import TrustedServicesFlow from "./TrustedServicesFlow";
import MidCta from "./MidCta";
import PortfolioSection from "./PortfolioSection";
import Testimonials from "./Testimonials";
import AboutSection from "./AboutSection";
import Contact from "./Contact";
import Footer from "./Footer";
import PageField from "./ui/page-field";

// Same SVG grain spec as the design prototype: fractalNoise 0.85, 2 octaves, 160px tile
const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const Home = () => {
  // clientWidth excludes the scrollbar — avoids canvas being wider than the viewport
  // which would silently add a horizontal scroll track on Windows.
  const [winW,  setWinW]  = useState(document.documentElement.clientWidth);
  const [heroH, setHeroH] = useState(document.documentElement.clientHeight);
  const [flowH, setFlowH] = useState(1360); // initial estimate; refined by ResizeObserver
  const flowRef = useRef(null);

  // Debounced viewport resize → canvas key changes, re-seeding the field
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        setWinW(document.documentElement.clientWidth);
        setHeroH(document.documentElement.clientHeight);
      }, 120);
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, []);

  // Measure the actual TrustedServicesFlow height so the canvas covers it exactly
  useEffect(() => {
    const el = flowRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setFlowH(Math.round(entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#070808" }}>
      <Header />

      {/* Unified particle field zone — ONE canvas behind hero + trusted + services.
          Hero and TrustedServicesFlow are both transparent so the canvas shows through.
          Only the X axis is clipped: overflowX:clip contains the marquee horizontally
          without creating a scroll container, while overflow-y stays at its default
          `visible` so this wrapper never becomes a vertical clipping context. The
          absolutely-positioned PageField canvas is sized as heroH + flowH, and flowH
          starts as an estimate that the ResizeObserver only corrects after first
          paint — clipping Y here would cut the canvas short whenever that estimate
          runs long. (The service-card hover-pills this allowance was originally
          written for were deleted with the card grid; the orbit slider that replaced
          them does NOT need it — its tiles are translate3d'd inside its own flat
          380px box, spanning roughly y=120..290 of that box across a full 360deg
          rotation at every container width.) */}
      <div style={{ position: "relative", overflowX: "clip" }}>
        <PageField
          key={`${winW}x${heroH}x${flowH}`}
          w={winW}
          heroH={heroH}
          flowH={flowH}
        />

        {/* Page-wide grain: sits above the canvas (z=1), below all content (z≥3).
            Single overlay means the grain texture never starts or stops at a section edge. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
            backgroundImage: GRAIN_URI, backgroundSize: "160px 160px",
            opacity: 0.05, mixBlendMode: "overlay",
          }}
        />

        {/* Transparent hero — canvas provides the backdrop */}
        <Hero />

        {/* Transparent flow section — canvas continues uninterrupted */}
        <div ref={flowRef}>
          <TrustedServicesFlow />
        </div>
      </div>

      {/* Remaining sections — unaffected by this redesign */}
      <MidCta />
      <PortfolioSection />
      <Testimonials />
      <AboutSection />
      <Contact />
      <Footer />
    </div>
  );
};

export default Home;
